import { useQuery } from '@tanstack/react-query';
import type { Matchday, Round } from '@uefadigital/panenka';
import { MatchDayType, MatchStatus } from '@uefadigital/panenka';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import Final from './components/Final';
import SemiFinal from './components/SemiFinal';

import { getMatchesQuery } from '@queries/matches';
import { Order } from '@shared-types/common.types';

import * as styles from './styles.module.scss';

export interface HpMatchFinalOptions {
  matchDay: Matchday;
  round: Round;
  sponsorTag?: string;
  confettiUrl: string;
  countdownDate: string;
  showLogoTitle: boolean;
  showHighlights?: boolean;
  disableTeamsLink?: boolean;
  hideDate?: boolean;
  hideRound?: boolean;
  hideContext?: boolean;
  hideSeeMore?: boolean;
  enableCustomSponsorImage?: boolean;
  countdownLabel?: string;
}

const HpMatchFinal = ({ options }: { options: HpMatchFinalOptions }) => {
  const isFinal = useMemo(
    () =>
      options.matchDay?.type.toUpperCase() === MatchDayType.FINAL &&
      options.round?.metaData.type.toUpperCase() === 'FINAL',
    [options.matchDay?.type, options.round?.metaData.type],
  );

  const { data: matches } = useQuery({
    ...getMatchesQuery({
      matchDayId: options.matchDay?.id,
      order: Order.ASC,
      offset: 0,
      limit: 10,
    }),
    enabled: !!options.matchDay?.id,
  });

  return (
    <div
      role='region'
      data-tracking-container='matches'
      className={clsx(
        'pk-container js-tracking-container',
        styles.hpMatchFinal,
        { sponsored: options.sponsorTag && isFinal && matches?.[0]?.status !== MatchStatus.FINISHED },
        { [styles.final]: isFinal },
      )}
    >
      {isFinal ? (
        <Final options={options} match={matches?.[0]} />
      ) : (
        <SemiFinal options={options} matches={matches?.slice(0, 2)} />
      )}
    </div>
  );
};

export default memo(HpMatchFinal);
